import React, { useContext, useCallback } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { I18nContext } from '@bingads-webui-react/i18n-context';
import { getHeadlinesCardSummaryDetails, getLongHeadlinesCardSummaryDetails, getDescriptionsCardSummaryDetails } from '@bingads-webui-campaign-react/validated-text-collection';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { HeadlineAssetCollection, DescriptionAssetCollection, LongHeadlineAssetCollection } from '@bingads-webui-campaign-react/pmax-text-input';

import { SectionDescription } from './section-description';
import { getTextAssetsStyles } from './text-assets-styles';

export const getHeadlinesSummaryDetails = (i18n, data) =>
  getHeadlinesCardSummaryDetails(i18n, data.Headlines);

export const getLongHeadlinesSummaryDetails = (i18n, data) =>
  getLongHeadlinesCardSummaryDetails(i18n, data.LongHeadlines);

export const getDescriptionsSummaryDetails = (i18n, data) =>
  getDescriptionsCardSummaryDetails(i18n, data.Descriptions);

const getIndexFromKey = (prefix, key) => key.split(prefix)[1];

export const getUpdatedPreviewKey = (prefix, previewKey, deleteKey) => {
  if (!previewKey || !deleteKey) {
    return previewKey;
  }

  const previewIndex = getIndexFromKey(prefix, previewKey);
  const deleteIndex = getIndexFromKey(prefix, deleteKey);

  if (deleteIndex < previewIndex) {
    return `${prefix}${previewIndex - 1}`;
  }

  return previewKey;
};

export const HeadlineAssets = React.memo(({
  setAddNewShortHeadlinesCallback, onSetHeadlinePreview, previewHeadlineKey,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getTextAssetsStyles);

  const onApplyRecommendation = useCallback((key) => {
    onSetHeadlinePreview(key);
  }, [onSetHeadlinePreview]);

  const onDeleteTextAsset = useCallback((key) => {
    onSetHeadlinePreview(getUpdatedPreviewKey('Headline', previewHeadlineKey, key));
  }, [onSetHeadlinePreview, previewHeadlineKey]);

  return (
    <div className={classNames('short-headline-section', classes.container)}>
      <SectionDescription text={i18n.getString(_TL_('Add up to {{number}} short headlines.'), { number: 15 })} />
      <HeadlineAssetCollection
        i18n={i18n}
        featureName="pmaxLite"
        isOptional={false}
        showDeleteButton
        onDeleteTextAsset={onDeleteTextAsset}
        onApplyRecommendation={onApplyRecommendation}
        setAddNewShortHeadlinesCallback={setAddNewShortHeadlinesCallback}
        onFocusTextAsset={onSetHeadlinePreview}
      />
    </div>
  );
});

HeadlineAssets.propTypes = {
  setAddNewShortHeadlinesCallback: PropTypes.func.isRequired,
  previewHeadlineKey: PropTypes.string.isRequired,
  onSetHeadlinePreview: PropTypes.func.isRequired,
};

export const LongHeadlineAssets = React.memo(({
  setAddNewLongHeadlinesCallback, onSetLongHeadlinePreview, previewLongHeadlineKey,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getTextAssetsStyles);

  const onApplyRecommendation = useCallback((key) => {
    onSetLongHeadlinePreview(key);
  }, [onSetLongHeadlinePreview]);

  const onDeleteTextAsset = useCallback((key) => {
    onSetLongHeadlinePreview(getUpdatedPreviewKey('LongHeadline', previewLongHeadlineKey, key));
  }, [onSetLongHeadlinePreview, previewLongHeadlineKey]);

  return (
    <div className={classNames('long-headline-section', classes.container)}>
      <SectionDescription text={i18n.getString(_TL_('Add up to 5 long headlines.'))} />
      <LongHeadlineAssetCollection
        i18n={i18n}
        isOptional={false}
        showDeleteButton
        onDeleteTextAsset={onDeleteTextAsset}
        onApplyRecommendation={onApplyRecommendation}
        setAddNewLongHeadlinesCallback={setAddNewLongHeadlinesCallback}
        onFocusTextAsset={onSetLongHeadlinePreview}
      />
    </div>
  );
});

LongHeadlineAssets.propTypes = {
  setAddNewLongHeadlinesCallback: PropTypes.func.isRequired,
  previewLongHeadlineKey: PropTypes.string.isRequired,
  onSetLongHeadlinePreview: PropTypes.func.isRequired,
};

export const DescriptionAssets = React.memo(({
  setAddNewDescriptionCallback, onSetDescriptionPreview, previewDescriptionKey,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getTextAssetsStyles);

  const onApplyRecommendation = useCallback((key) => {
    onSetDescriptionPreview(key);
  }, [onSetDescriptionPreview]);

  const onDeleteTextAsset = useCallback((key) => {
    onSetDescriptionPreview(getUpdatedPreviewKey('Description', previewDescriptionKey, key));
  }, [onSetDescriptionPreview, previewDescriptionKey]);

  return (
    <div className={classNames('description-section', classes.container)}>
      <SectionDescription text={i18n.getString(_TL_('Add up to 5 descriptions.'))} />
      <DescriptionAssetCollection
        i18n={i18n}
        isOptional={false}
        showDeleteButton
        onDeleteTextAsset={onDeleteTextAsset}
        onApplyRecommendation={onApplyRecommendation}
        setAddNewDescriptionCallback={setAddNewDescriptionCallback}
        onFocusTextAsset={onSetDescriptionPreview}
      />
    </div>
  );
});

DescriptionAssets.propTypes = {
  setAddNewDescriptionCallback: PropTypes.func.isRequired,
  previewDescriptionKey: PropTypes.string.isRequired,
  onSetDescriptionPreview: PropTypes.func.isRequired,
};
